import Vue from 'vue';
import { getStoreBuilder, BareActionContext } from 'vuex-typex';
import Vuex, { Store } from 'vuex';
import axios from 'axios';
import jwtDecode from 'jwt-decode';

// Import any store modules
import { initialSubState } from '@/state/modules/sub1';
import constantsStore, { initialConstantsState } from './modules/constants';
import { initialOpportunitiesState } from './modules/opportunities';
import optionsStore, { initialOptionsState } from './modules/options';
import { initialPartnersState } from './modules/partners';

Vue.use(Vuex);

// Prepare store builder for use throughout
const storeBuilder = getStoreBuilder<IRootStore>();

// === STATE
// Prepare initial state, store builder
const initialState: IRootStore = {
	// Loading state
	loading: false,

	// Session/config
	config: null,
	constants: initialConstantsState,
	userInfo: null,

	// Global data
	messages: [],
	route: null,
	selectOptions: initialOptionsState,

	// Store modules
	sub: initialSubState,
	opportunities: initialOpportunitiesState,
	partners: initialPartnersState,
};

// === GETTERS
const USER_IS_ADMIN_GETTER = storeBuilder.read(
	({ }, getters: IDictionary<any>) => {
		const roles = getters.getUserRoles;
		return roles.indexOf('AppRole-PartnerAuth-OfficeAdmin') !== -1
			|| roles.indexOf('AppRole-PartnerAuth-OrgAdmin') !== -1
			|| roles.indexOf('AppRole-PartnerAuth-SuperAdmin') !== -1;
	},
	'getUserIsAdmin',
);
const USER_IS_SUPER_ADMIN_GETTER = storeBuilder.read(
	({ }, getters: IDictionary<any>) => {
		const roles = getters.getUserRoles;
		return roles.indexOf('AppRole-PartnerAuth-SuperAdmin') !== -1;
	},
	'getUserIsSuperAdmin',
);
const USER_ROLES_GETTER = storeBuilder.read(
	(state: IRootStore) => {
		const userInfo = state.userInfo;
		if (!userInfo) {
			return [];
		}
		const roles = userInfo.AppRole || [];
		return roles;
	},
	'getUserRoles',
);
const IS_IT_USER_GETTER = storeBuilder.read(
	(state: IRootStore) => {
		const userInfo = state.userInfo;
		if (!userInfo) {
			return false;
		}
		const costCenter = userInfo.costCenter || "";
		const isIt = costCenter === "85100210" || costCenter === "85600210";
		return isIt;
	},
	'getIsItUser',
);
const USER_MANAGER_URL_GETTER = storeBuilder.read(
	(state: IRootStore) => {
		const config = state.config;
		if (!config) {
			return '';
		}
		const roles = config.userManagerUrl || '';
		return roles;
	},
	'getUserManagerUrl',
);
const USER_IS_COMPLIANT_GETTER = storeBuilder.read(
	(state: IRootStore) => {
		const userInfo = state.userInfo;
		if (!userInfo) {
			return true;
		}
		const isCompliant = userInfo.isCompliant;
		return isCompliant;
	},
	'getUserIsCompliant',
);

// === MUTATIONS
const SET_LOADING_MUTATION = (state: IRootStore, data: boolean) => {
	state.loading = data;
};
const SET_CONFIG_MUTATION = (state: IRootStore, data: ISiteConfig) => {
	state.config = data;
};
const CLEAR_CONFIG_MUTATION = (state: IRootStore) => {
	state.config = null;
};
interface ISetUserDataParam {
	user: IUser;
	accessToken: string;
}
const SET_USER_MUTATION = (state: IRootStore, data: ISetUserDataParam) => {
	const accessToken: IDictionary<any> = jwtDecode(data.accessToken);
	const appRole = ((accessToken.AppRole || []) as string[]).concat(accessToken["AppRole-PartnerAuth"] || []);
	const isCompliant = accessToken.isCompliant === false ? false : true;
	const userInfo: IUser = {
		...data.user,
		AppRole: appRole,
		employeeId: accessToken.employeeid,
		costCenter: accessToken.costCenter,
		office: accessToken.partnerOffice,
		partnerUniqueId: accessToken.partnerUniqueId,
		isCompliant: isCompliant,
	};
	state.userInfo = userInfo;
};
const CLEAR_USER_MUTATION = (state: IRootStore) => {
	state.userInfo = null;
};
const ADD_MESSAGE_MUTATION = (state: IRootStore, data: IMessage) => {
	state.messages.push(data);
};
const REMOVE_MESSAGE_MUTATION = (state: IRootStore, messageId: string) => {
	state.messages = state.messages.filter(msg => msg.id !== messageId);
};

// === ACTIONS
type rootStoreContext = BareActionContext<IRootStore, IRootStore>;
const GET_CONFIG_ACTION = (store: rootStoreContext, url: string = '/config.json') => {
	const { state } = store;

	return fetchConfig(url)
		.then((data) => {
			// Only set store if not already set
			if (!state.config) {
				rootStore.commitSetConfig(data);
				if (data.optionOverrides) {
					optionsStore.dispatchOverrideOptions(data.optionOverrides);
				}
				if (data.constantOverrides) {
					constantsStore.dispatchOverrideConstants(data.constantOverrides);
				}
			}
			return data;
		});
};

let maxMessageId = 0;
const CREATE_MESSAGE_ACTION = ({ }: rootStoreContext, data: IMessage) => {
	// Create unique id for the message
	let messageId = (data.type ? data.type : 'msg');
	messageId = `${messageId}_${maxMessageId}`;
	maxMessageId += 1;

	// Commit message
	rootStore.commitAddMessage({
		...data,
		id: messageId,
	});

	// Set timer to remove message
	const timeout = (typeof data.timeout !== 'undefined') ? data.timeout : 3000;
	if (timeout) {
		setTimeout(() => { rootStore.commitRemoveMessage(messageId); }, timeout);
	}

	// Return resolved promise with the id of the message
	return Promise.resolve(messageId);
};
const REMOVE_MESSAGE_ACTION = ({ }: rootStoreContext, messageId: string) => {
	rootStore.commitRemoveMessage(messageId);
};

// === SERVICES
type IConfigPromise = Promise<any>;
const configPromises: { [key: string]: IConfigPromise } = {};
const fetchConfig = (url: string): IConfigPromise => {
	// 1. Check if we already have the config promise
	if (configPromises[url] !== void 0) {
		return configPromises[url];
	}

	// 2. Fetch the config and extract data from response
	configPromises[url] = axios.get(url).then(response => response.data);

	// 3. Return promise
	return configPromises[url];
};

// Prepare this module's state for export
const stateGetter = storeBuilder.state();

// exported module interface
const rootStore = {
	// state
	get state() { return stateGetter(); },

	// getters
	get getUserRoles() { return USER_ROLES_GETTER(); },
	get getUserIsAdmin() { return USER_IS_ADMIN_GETTER(); },
	get getUserIsSuperAdmin() { return USER_IS_SUPER_ADMIN_GETTER(); },
	get getIsItUser() { return IS_IT_USER_GETTER(); },
	get getUserManagerUrl() { return USER_MANAGER_URL_GETTER(); },
	get getUserIsCompliant() { return USER_IS_COMPLIANT_GETTER(); },

	// mutations
	commitSetLoading: storeBuilder.commit(SET_LOADING_MUTATION, 'SET_LOADING'),
	commitSetConfig: storeBuilder.commit(SET_CONFIG_MUTATION, 'SET_CONFIG'),
	commitClearConfig: storeBuilder.commit(CLEAR_CONFIG_MUTATION, 'CLEAR_CONFIG'),
	commitSetUser: storeBuilder.commit(SET_USER_MUTATION, 'SET_USER'),
	commitClearUser: storeBuilder.commit(CLEAR_USER_MUTATION, 'CLEAR_USER'),
	commitAddMessage: storeBuilder.commit(ADD_MESSAGE_MUTATION, 'ADD_MESSAGE'),
	commitRemoveMessage: storeBuilder.commit(REMOVE_MESSAGE_MUTATION, 'REMOVE_MESSAGE'),

	// action
	dispatchGetConfig: storeBuilder.dispatch(GET_CONFIG_ACTION, 'getConfig'),
	dispatchCreateMessage: storeBuilder.dispatch(CREATE_MESSAGE_ACTION, 'createMessage'),
	dispatchRemoveMessage: storeBuilder.dispatch(REMOVE_MESSAGE_ACTION, 'removeMessage'),
};

// Export default store object
export default rootStore;

// Export store instance to attach to app
export const instance: Store<IRootStore> = storeBuilder.vuexStore({
	state: initialState,
});
